import {
  Box,
  Button,
  ButtonProps,
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import tokens from '@verifime/design-tokens';
import { useState } from 'react';
import { ColoredIcon, TIconName, useSemanticColor, StatusColor } from './ColoredIconAndChip';
import { EntityStatus } from '@verifime/utils';

// Simple semantic color specification - callers don't need to know internal structure
export type SemanticColorSpec = 
  | { semantic: 'success' }
  | { semantic: 'warning' }
  | { semantic: 'error' }
  | { semantic: 'info' }
  | { semantic: 'disabled' }
  | { semantic: 'active' };

// Pure mapping, no hook call: separated out so useColorFromSemanticSpec can make a single,
// unconditional useSemanticColor call instead of branching on which hook call to make
// (react-hooks/rules-of-hooks forbids the latter, even though every branch here called exactly
// one hook and so never actually broke at runtime).
export function mapSemanticSpecToColorArgs(
  spec: SemanticColorSpec,
): Parameters<typeof useSemanticColor>[0] {
  switch (spec.semantic) {
    case 'success':
      return { category: 'status', value: 'success' };
    case 'warning':
      return { category: 'status', value: 'warning' };
    case 'error':
      return { category: 'status', value: 'error' };
    case 'info':
      return { category: 'status', value: 'info' };
    case 'disabled':
      return { category: 'interactive', value: 'disabled' };
    case 'active':
      return { category: 'interactive', value: 'enabled' };
  }
}

function useColorFromSemanticSpec(spec: SemanticColorSpec): StatusColor {
  return useSemanticColor(mapSemanticSpecToColorArgs(spec));
}

export type TActions<T extends string | Record<string, unknown>> = {
  [action in T extends string ? string : keyof T]: {
    iconName: TIconName;
    color: SemanticColorSpec;
    onActionClick?: (action: T extends string ? string : keyof T) => void;
  };
};

export default function ActionsButton<T extends string | Record<string, unknown>>({
  buttonName,
  actions,
  ...props
}: Readonly<
  {
    buttonName: string;
    actions: TActions<T>;
  } & Omit<ButtonProps, 'onClick'>
>) {
  const [isShowActions, setIsShowActions] = useState(false);
  return (
    <Box sx={{ position: 'relative' }}>
      <Button variant="contained" onClick={() => setIsShowActions(!isShowActions)} {...props}>
        <Typography variant="button">{buttonName}</Typography>
        <ColoredIcon iconName="keyboardArrowDownRounded" />
      </Button>

      {isShowActions && Object.keys(actions || {}).length > 0 && (
        <Paper
          sx={{
            position: 'absolute',
            minWidth: '150px',
            width: '80%',
            top: '50%',
            right: 0,
            zIndex: 2,
          }}
        >
          <MenuList sx={{ padding: `${tokens.spacingXs} 0` }}>
            {(Object.entries(actions) as [keyof TActions<T>, TActions<T>[keyof TActions<T>]][]).map(
              ([action, settings], index) => {
                return (
                  <ActionsMenuItem
                    key={action + index}
                    action={action}
                    settings={settings}
                    onClose={() => setIsShowActions(false)}
                  />
                );
              },
            )}
          </MenuList>
        </Paper>
      )}
    </Box>
  );
}

function ActionsMenuItem({
  action,
  settings,
  onClose,
}: {
  action: string | number | symbol;
  settings: { iconName: TIconName; color: SemanticColorSpec; onActionClick?: (action: any) => void };
  onClose: () => void;
}) {
  const color = useColorFromSemanticSpec(settings.color);
  return (
    <MenuItem
      onClick={() => {
        settings.onActionClick?.(action);
        onClose();
      }}
    >
      <Stack direction="row" gap={tokens.spacingXs}>
        <Box sx={{ flex: 1 }}>
          {/*
           * TODO: UX Review Required
           * This uses a non-standard color pattern: fill || border || icon
           * Standard standalone pattern is: fill || icon (via statusColor prop)
           * The 'border' fallback is unclear - may have been added without UX review.
           * See ActionsButton.stories.tsx for visual reference to discuss with designer.
           */}
          <ColoredIcon
            iconName={settings.iconName}
            sx={{ fill: color?.fill || color?.border || color?.icon }}
          />
        </Box>
        <Typography
          sx={{
            flex: 2,
            color: color?.fill,
          }}
        >
          {String(action)}
        </Typography>
      </Stack>
      <Divider />
    </MenuItem>
  );
}
